import { isLoggedInAtom, isLoggingOutAtom, userInfoAtom } from 'app/atoms';
import { apiEndpointManager } from 'app/lib/axios/api-endpoint';
import { StorageKey, storage } from 'app/lib/storage';
import { useAllAddresses } from 'app/network/address';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useLogoutRoutine } from './use-logout-routine';

export function useApiRegionMigration() {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isLoggingOut = useAtomValue(isLoggingOutAtom);
  const setIsLoggingOut = useSetAtom(isLoggingOutAtom);
  const user = useAtomValue(userInfoAtom);
  const logoutRoutine = useLogoutRoutine();
  const logoutRoutineRef = useRef(logoutRoutine);
  useEffect(() => {
    logoutRoutineRef.current = logoutRoutine;
  }, [logoutRoutine]);
  const isHandlingMigration = useRef(false);
  const needsAddressFallback = user?.region === null || user?.region === undefined;
  const { data: addresses } = useAllAddresses({ enabled: needsAddressFallback });

  const isUsHomeRegion =
    user?.region === 'US' || (needsAddressFallback && addresses?.[0]?.country?.toUpperCase() === 'US');

  useEffect(() => {
    if (!isLoggedIn || isLoggingOut || !user?.id || !isUsHomeRegion || isHandlingMigration.current) return;
    if (!apiEndpointManager.getBaseUrlForRegion('US')) return;

    isHandlingMigration.current = true;
    void apiEndpointManager
      .isRegionPreferred('US')
      .then(async (isUsPreferred) => {
        if (isUsPreferred) {
          await storage.saveString(StorageKey.RegionMigrationAttempt);
          return;
        }

        const previousAttempt = await storage.readString(StorageKey.RegionMigrationAttempt);
        const [previousUserId, timestamp] = previousAttempt?.split(':') ?? [];
        if (previousUserId === user.id && Date.now() - Number(timestamp) < 15 * 60 * 1000) return;
        await storage.saveString(StorageKey.RegionMigrationAttempt, `${user.id}:${String(Date.now())}`);

        setIsLoggingOut(true);
        await logoutRoutineRef.current();
        apiEndpointManager.preferForRegion('US');
      })
      .finally(() => {
        isHandlingMigration.current = false;
      });
  }, [isLoggedIn, isLoggingOut, isUsHomeRegion, user?.id, setIsLoggingOut]);
}
